/**
 * Per-SF-plan estimate binding cache + API helpers.
 *
 * SF source plans (25/26 inventory) don't have a Snowflake PLAN record,
 * so their per-plan estimate-version binding lives in the dedicated
 * SF_PLAN_ESTIMATE_BINDING table keyed on the Salesforce SF id.
 *
 * The whole table is small (one row per SF plan the user has manually
 * rebound) so we hydrate it all at boot and keep an in-memory cache
 * mirrored from there. Read helpers stay synchronous so MediaPlanBuilder's
 * activeVersion useEffect doesn't have to become async.
 *
 * 26/27 Snowflake-saved plans persist their binding via
 * PLAN_VERSION.estimate_version_id and do NOT use this table.
 */

import { SfPlanEstimateBinding } from './types';
import { authAxios } from '../utils/session_utils';
import { createCacheHydration } from '../utils/cache-hydration';

const API_BASE = process.env.REACT_APP_BACK_END_API;

// ─── In-memory cache ─────────────────────────────────────────────────────
//
// Lifecycle (hydrate / isHydrated / retry / onChange / error tracking) is
// owned by the shared `createCacheHydration` helper — see
// utils/cache-hydration.ts. This module owns just the Map itself.

const bindingCache = new Map<string, SfPlanEstimateBinding>();

const hydration = createCacheHydration<SfPlanEstimateBinding>({
  resourceName: 'SF plan bindings',
  fetch: async () => {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/sf-plan-estimate-bindings`,
    });
    return response?.data?.bindings ?? [];
  },
  apply: (bindings) => {
    bindingCache.clear();
    bindings.forEach(b => bindingCache.set(b.sfPlanSfId, b));
  },
});

/**
 * Hydrate the cache from Snowflake. Safe to call multiple times — shares
 * one fetch. Called once from App.tsx (or the MediaPlanBuilder boot path)
 * alongside hydrateEstimateVersions.
 */
export const hydrateSfPlanBindings = hydration.hydrate;

/** True once the initial hydrate has completed (success or failure). */
export const isSfBindingsHydrated = hydration.isHydrated;

/** Last hydrate error message, or null if hydrate succeeded. */
export const getSfBindingsHydrateError = hydration.getError;

/** Subscribe to hydrate / cache changes. Returns an unsubscribe function. */
export const onSfBindingsChange = hydration.onChange;

// `retrySfBindingsHydrate` isn't currently exposed because the callers
// (App.tsx + MediaPlanBuilder) just call `hydrateSfPlanBindings()` again,
// which is a no-op on success and re-runs on error via the helper. If a
// retry-banner ever lands for this cache, swap to `hydration.retry`.

// ─── Read helpers (sync) ─────────────────────────────────────────────────

/**
 * Get the binding for an SF plan by its SF id, or null if no explicit
 * binding has been set (which means the plan should resolve to the
 * default "latest upfront for the plan's year").
 */
export function getSfPlanBinding(sfPlanSfId: string): SfPlanEstimateBinding | null {
  if (!sfPlanSfId) return null;
  return bindingCache.get(sfPlanSfId) ?? null;
}

/** Returns all bindings — used for debugging / dev tools. */
export function listSfPlanBindings(): SfPlanEstimateBinding[] {
  const arr: SfPlanEstimateBinding[] = [];
  bindingCache.forEach(b => arr.push(b));
  return arr;
}

// ─── Write helpers (async — POST and refresh cache) ──────────────────────

/**
 * Upsert a binding. POSTs to the backend, then mirrors the change into
 * the local cache + notifies listeners on success. Caller flow: open
 * pill → pick version → Save → call this → button hides.
 */
export async function setSfPlanBinding(
  sfPlanSfId: string,
  estimateVersionId: number,
  updatedBy?: string,
): Promise<{ ok: true; binding: SfPlanEstimateBinding } | { ok: false; error: string }> {
  if (!sfPlanSfId) return { ok: false, error: 'Missing SF plan SF id.' };
  // Positive, not merely finite. Synthetic versions (the All Plans merge, the
  // late-quarter merge) carry negative ids that match no EST_VERSION row;
  // persisting one would bind the plan to nothing. Callers are expected to
  // unwrap via baseVersionIdOf, but a bare `isFinite` would wave a synthetic
  // straight through, so enforce it here too.
  if (!Number.isFinite(estimateVersionId) || estimateVersionId <= 0) {
    return { ok: false, error: 'estimateVersionId must be a positive number.' };
  }
  try {
    const response = await authAxios({
      method: 'PUT',
      url: `${API_BASE}/snowflake/sf-plan-estimate-bindings/${encodeURIComponent(sfPlanSfId)}`,
      data: { estimateVersionId, updatedBy: updatedBy ?? null },
    });
    const persisted: SfPlanEstimateBinding | undefined = response?.data?.binding;
    if (!persisted) return { ok: false, error: 'Backend did not return the saved binding.' };

    // Mirror into the cache so synchronous reads see the change immediately
    // — no need to re-hydrate the whole table.
    bindingCache.set(persisted.sfPlanSfId, persisted);
    hydration.notifyChange();
    return { ok: true, binding: persisted };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to save binding.';
    return { ok: false, error: msg };
  }
}

/**
 * Remove a binding — reverts the SF plan to the default resolution
 * ("latest upfront for the plan's year"). Idempotent: returns ok even
 * when no binding existed.
 */
export async function clearSfPlanBinding(
  sfPlanSfId: string,
): Promise<{ ok: true; removed: boolean } | { ok: false; error: string }> {
  if (!sfPlanSfId) return { ok: false, error: 'Missing SF plan SF id.' };
  try {
    const response = await authAxios({
      method: 'DELETE',
      url: `${API_BASE}/snowflake/sf-plan-estimate-bindings/${encodeURIComponent(sfPlanSfId)}`,
    });
    const removed = Boolean(response?.data?.removed);
    bindingCache.delete(sfPlanSfId);
    hydration.notifyChange();
    return { ok: true, removed };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to remove binding.';
    return { ok: false, error: msg };
  }
}
